/**
 * Gate 5 — Cost-Weighted Threshold
 *
 * Rejects high-cost actions paired with low confidence. The
 * actionable cost is proxied by materiality band. As the cost
 * of being wrong scales up with materiality, the required
 * confidence threshold scales proportionally.
 *
 * Cost-weighted floors are more conservative than the raw
 * confidence floors (Gate 3) to capture the asymmetry between
 * cost of error and cost of abstention.
 *
 * Trip routes to abstention with reason='cost_weighted_threshold_not_met'.
 */

export const COST_WEIGHTED_FLOORS = {
  ROUTINE: 0.30,
  ELEVATED: 0.55,
  MATERIAL: 0.72,
  EXISTENTIAL: 0.88,
};

/**
 * @param {import('../disciplineEnforcer.js').EnforcerEnvelope} envelope
 * @param {object} [config]
 * @param {Record<string,number>} [config.costFloorOverrides]
 * @returns {import('../disciplineEnforcer.js').GateResult}
 */
export function checkCostWeightedThreshold(envelope, config = {}) {
  const band = envelope.context?.materialityBand || 'ROUTINE';
  const floors = { ...COST_WEIGHTED_FLOORS, ...(config.costFloorOverrides || {}) };
  const floor = floors[band] ?? COST_WEIGHTED_FLOORS.ROUTINE;

  if (envelope.confidence == null) {
    return { gateName: 'cost_weighted_threshold', verdict: 'pass', reason: null, payload: { band, floor, confidence: null } };
  }

  if (envelope.confidence < floor) {
    return {
      gateName: 'cost_weighted_threshold',
      verdict: 'trip',
      reason: 'cost_weighted_threshold_not_met',
      payload: { band, floor, confidence: envelope.confidence },
    };
  }

  return {
    gateName: 'cost_weighted_threshold',
    verdict: 'pass',
    reason: null,
    payload: { band, floor, confidence: envelope.confidence },
  };
}
